import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Package, FileDown, Send, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";

const FASES = ["Cold", "Terugbellen", "Sample opsturen", "Sample opgestuurd", "Onderhandeling", "Deal!", "Afgevallen"];

const fmtEUR = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const fmtD = (iso) => { if (!iso) return ""; const d = new Date(iso); if (isNaN(d)) return iso.slice(0,10); const dd=String(d.getDate()).padStart(2,"0"); const mm=String(d.getMonth()+1).padStart(2,"0"); return `${dd}-${mm}-${d.getFullYear()}`; };
const dateInput = (iso) => { if (!iso) return ""; const d = new Date(iso); if (isNaN(d)) return ""; return d.toISOString().slice(0,10); };

function HeaderDialog({ kind, value, relatie_id, onSaved, trigger }) {
  // kind: "kans" | "factuur"
  const empty = kind === "kans"
    ? { naam: "", fase: "", bedrag: 0, datum: dateInput(new Date().toISOString()) }
    : { type: "factuur", factuurnummer: "", bedrag: 0, datum: dateInput(new Date().toISOString()) };
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(value ? { ...value, datum: dateInput(value.datum), bedrag: value.bedrag || 0 } : empty);
  useEffect(() => { if (open) setF(value ? { ...value, datum: dateInput(value.datum), bedrag: value.bedrag || 0 } : empty); /* eslint-disable-next-line */ }, [open]);

  const submit = async () => {
    try {
      const url = kind === "kans" ? "/verkoopkansen" : "/verkoopfacturen";
      const payload = { ...f, bedrag: parseFloat(String(f.bedrag).toString().replace(",", ".")) || 0,
                        datum: f.datum ? new Date(f.datum).toISOString() : null };
      if (value?.id) await api.patch(`${url}/${value.id}`, payload);
      else await api.post(url, { ...payload, relatie_id });
      toast.success("Opgeslagen"); setOpen(false); onSaved();
    } catch { toast.error("Opslaan mislukt"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{value ? "Bewerken" : "Nieuw"} — {kind === "kans" ? "verkoopkans" : "factuur"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {kind === "kans" ? (
            <>
              <div><Label>Naam</Label><Input data-testid="kans-naam" value={f.naam || ""} onChange={(e)=>setF({...f,naam:e.target.value})} /></div>
              <div><Label>Fase</Label>
                <Select value={f.fase || "Cold"} onValueChange={(v)=>setF({...f,fase:v})}>
                  <SelectTrigger data-testid="kans-fase"><SelectValue /></SelectTrigger>
                  <SelectContent>{FASES.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Type</Label>
                  <Select value={f.type} onValueChange={(v)=>setF({...f,type:v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["factuur","creditfactuur"].map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Factuurnummer</Label><Input value={f.factuurnummer || ""} onChange={(e)=>setF({...f,factuurnummer:e.target.value})} /></div>
              </div>
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Bedrag (€)</Label><Input type="number" step="0.01" data-testid="kf-bedrag" value={f.bedrag} onChange={(e)=>setF({...f,bedrag:e.target.value})} /></div>
            <div><Label>Datum</Label><Input type="date" data-testid="kf-datum" value={f.datum || ""} onChange={(e)=>setF({...f,datum:e.target.value})} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="kf-submit">Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RegelDialog({ kind, parent_id, value, onSaved, trigger }) {
  const empty = { code: "", product: "", aantal: 1, inkoopprijs: 0, verkoopprijs: 0 };
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(value || empty);
  useEffect(() => { if (open) setF(value || empty); /* eslint-disable-next-line */ }, [open]);

  const submit = async () => {
    try {
      const url = kind === "kans" ? "/verkoopkansregels" : "/verkoopfactuurregels";
      const num = (v) => parseFloat(String(v).toString().replace(",", ".")) || 0;
      const payload = { ...f, aantal: num(f.aantal), inkoopprijs: num(f.inkoopprijs), verkoopprijs: num(f.verkoopprijs) };
      if (value?.id) await api.patch(`${url}/${value.id}`, payload);
      else await api.post(url, { ...payload, [kind === "kans" ? "verkoopkans_id" : "factuur_id"]: parent_id });
      toast.success("Regel opgeslagen"); setOpen(false); onSaved();
    } catch { toast.error("Opslaan mislukt"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{value ? "Regel bewerken" : "Nieuwe regel"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Code</Label><Input data-testid="reg-code" value={f.code || ""} onChange={(e)=>setF({...f,code:e.target.value})} /></div>
            <div className="col-span-2"><Label>Product</Label><Input data-testid="reg-product" value={f.product || ""} onChange={(e)=>setF({...f,product:e.target.value})} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Aantal</Label><Input type="number" step="0.01" value={f.aantal} onChange={(e)=>setF({...f,aantal:e.target.value})} /></div>
            <div><Label>Inkoop (€)</Label><Input type="number" step="0.01" value={f.inkoopprijs} onChange={(e)=>setF({...f,inkoopprijs:e.target.value})} /></div>
            <div><Label>Verkoop (€)</Label><Input type="number" step="0.01" data-testid="reg-verkoop" value={f.verkoopprijs} onChange={(e)=>setF({...f,verkoopprijs:e.target.value})} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={()=>setOpen(false)}>Annuleren</Button>
          <Button onClick={submit} data-testid="reg-submit">Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VerkoopList({ kind, items, regels, openId, onToggle, onReload, relatie_id, isAdmin }) {
  const url = kind === "kans" ? "/verkoopkansen" : "/verkoopfacturen";
  const regelUrl = kind === "kans" ? "/verkoopkansregels" : "/verkoopfactuurregels";
  const PER_PAGINA = 20;
  const [pagina, setPagina] = useState(0);
  const paginas = Math.max(1, Math.ceil((items?.length || 0) / PER_PAGINA));
  const huidige = Math.min(pagina, paginas - 1);
  const zichtbaar = (items || []).slice(huidige * PER_PAGINA, huidige * PER_PAGINA + PER_PAGINA);

  const remove = async (id) => {
    if (!window.confirm("Weet je het zeker?")) return;
    await api.delete(`${url}/${id}`);
    toast.success("Verwijderd"); onReload();
  };
  const removeRegel = async (id) => {
    await api.delete(`${regelUrl}/${id}`);
    toast.success("Regel verwijderd"); onToggle(openId); // herlaad regels
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <HeaderDialog kind={kind} relatie_id={relatie_id} onSaved={onReload}
          trigger={<Button size="sm" data-testid={`new-${kind}-btn`}><Plus className="h-4 w-4 mr-1" />Nieuw</Button>} />
      </div>
      <div className="bg-card border border-border rounded-xl divide-y divide-border">
        {zichtbaar.map(it => (
          <div key={it.id} data-testid={`${kind}-${it.id}`}>
            {kind === "kans" ? (
              <Link to={`/verkoopkansen/${it.id}`} state={{ terug: "/verkoopkansen", terugLabel: "Verkoopkansen" }} className="block p-4 flex items-center justify-between gap-3 hover:bg-secondary/40 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.naam || "(zonder naam)"}</div>
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <Badge variant={it.fase === "Deal!" ? "default" : it.fase === "Afgevallen" ? "destructive" : "outline"} className="capitalize">{it.fase}</Badge>
                    <span>{it.aantal_regels} regel(s)</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-medium">{fmtEUR(it.bedrag)}</div>
                  <div className="text-xs text-muted-foreground">{fmtD(it.datum)}</div>
                </div>
              </Link>
            ) : (
              <Link to={`/verkoopfacturen/${it.id}`} className="block p-4 flex items-center justify-between gap-3 hover:bg-secondary/40 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={it.type === "creditfactuur" ? "destructive" : "outline"} className="capitalize">{it.type}</Badge>
                    <span className="font-medium">{it.factuurnummer || it.id.slice(0,8)}</span>
                    {it.status_factuur === "nog_te_factureren" && <Badge variant="secondary">Wachtrij</Badge>}
                  </div>
                  <div className="text-sm text-muted-foreground mt-0.5">{it.aantal_regels} regel(s)</div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-medium ${it.type === "creditfactuur" ? "text-rose-700" : ""}`}>
                    {(() => {
                      const isCredit = it.type === "creditfactuur";
                      const v = Math.abs(Number(it.bedrag) || 0);
                      return fmtEUR(isCredit ? -v : v);
                    })()}
                  </div>
                  <div className="text-xs text-muted-foreground">{fmtD(it.datum)}</div>
                </div>
              </Link>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-muted-foreground text-sm">Nog geen {kind === "kans" ? "verkoopkansen" : "facturen"}.</div>}
      </div>
      {items.length > PER_PAGINA && (
        <div className="flex items-center justify-between text-xs text-muted-foreground"
          data-testid={`${kind}-paginering`}>
          <span>
            {huidige * PER_PAGINA + 1}–{Math.min(items.length, (huidige + 1) * PER_PAGINA)} van {items.length}
            {kind === "kans" ? " kansen" : " facturen"} (nieuwste eerst)
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={huidige === 0}
              onClick={() => setPagina(huidige - 1)} data-testid={`${kind}-vorige`}>
              <ChevronLeft className="h-4 w-4" /> Nieuwer
            </Button>
            <span>{huidige + 1} / {paginas}</span>
            <Button variant="outline" size="sm" disabled={huidige >= paginas - 1}
              onClick={() => setPagina(huidige + 1)} data-testid={`${kind}-volgende`}>
              Ouder <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
